/* The map of the path.
 *
 * One card per chapter, drawn at its authored coordinates inside a world that
 * is panned and zoomed by a single transform. Zoomed out the cards are a map of
 * chapters; zoomed in they show the steps inside each one — that switch is
 * `data-lod` on the container, and the CSS does the rest.
 *
 * On touch the graph is a map only: pan, zoom and the pip targets are disabled,
 * exactly as before.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NODES } from '../../data/nodes';
import { byId } from '../../engine/progress';
import { WORLD, fitView, focusView, levelOfDetail, zoomAt, type View } from '../../engine/graph';
import { useElementSize, useNarrow } from '../../hooks/useMedia';
import { useApp } from '../../state/AppProvider';
import { EdgeLayer } from './EdgeLayer';
import { GraphNode } from './GraphNode';
import { Minimap } from './Minimap';
import { SubjectLabels } from './SubjectLabels';

export function PathGraph() {
  const { t } = useTranslation();
  const { sel, selectNode } = useApp();
  const canvasRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; vx: number; vy: number } | null>(null);
  const [view, setView] = useState<View>({ x: 0, y: 0, k: 1 });
  const { w, h } = useElementSize(canvasRef);
  const narrow = useNarrow();

  const fit = useCallback(() => {
    const el = canvasRef.current;
    if (!el) return;
    if (el.clientWidth < 40 || el.clientHeight < 40) return;
    setView(fitView(el.clientWidth, el.clientHeight, narrow));
  }, [narrow]);

  useEffect(() => {
    fit();
  }, [fit, w, h]);

  /* Coming back from a chapter re-frames the whole path, as it did before. */
  const wasLesson = useRef(false);
  useEffect(() => {
    if (wasLesson.current && !sel) fit();
    wasLesson.current = !!sel;
  }, [sel, fit]);

  /* Wheel is registered natively so preventDefault is allowed: React attaches
     wheel listeners passively, and the page must not scroll under the zoom. */
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      if (narrow) return;
      event.preventDefault();
      const rect = el.getBoundingClientRect();
      setView((v) => zoomAt(v, event.deltaY < 0 ? 1.12 : 1 / 1.12, event.clientX - rect.left, event.clientY - rect.top));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [narrow]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (narrow) return;
    const target = event.target as HTMLElement;
    if (target.closest('.node') || target.closest('button')) return;
    drag.current = { x: event.clientX, y: event.clientY, vx: view.x, vy: view.y };
    canvasRef.current?.classList.add('dragging');
    canvasRef.current?.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = drag.current;
    if (!start) return;
    setView((v) => ({ ...v, x: start.vx + (event.clientX - start.x), y: start.vy + (event.clientY - start.y) }));
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current = null;
    canvasRef.current?.classList.remove('dragging');
    try {
      canvasRef.current?.releasePointerCapture(event.pointerId);
    } catch {
      /* the pointer may already be gone */
    }
  };

  /* Keyboard focus on a card brings it into the middle of the map. */
  const focusCard = useCallback(
    (id: string) => {
      const el = canvasRef.current;
      const node = byId(id);
      if (!el || !node || narrow || sel) return;
      setView((v) => focusView(node, v, el.clientWidth, el.clientHeight));
    },
    [narrow, sel],
  );

  const onMinimapPan = useCallback((wx: number, wy: number) => {
    const el = canvasRef.current;
    if (!el) return;
    setView((v) => ({ ...v, x: el.clientWidth / 2 - wx * v.k, y: el.clientHeight / 2 - wy * v.k }));
  }, []);

  return (
    <section className="canvas-wrap" data-od-id="path-graph">
      <div
        className="canvas"
        id="canvas"
        ref={canvasRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <div
          className="world"
          id="world"
          style={{
            width: WORLD.w,
            height: WORLD.h,
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})`,
          }}
        >
          <EdgeLayer />
          <div
            className="nodes"
            id="nodes"
            data-lod={levelOfDetail(view.k)}
            onFocus={(event) => {
              const card = (event.target as HTMLElement).closest('.node');
              if (card) focusCard(card.getAttribute('data-id') ?? '');
            }}
          >
            {NODES.map((node) => (
              <GraphNode key={node.id} node={node} onOpen={selectNode} />
            ))}
          </div>
        </div>

        <SubjectLabels view={view} />
      </div>

      <div className="canvas-tools">
        <Minimap view={view} size={{ w, h }} onPan={onMinimapPan} />
        <div className="zoom" data-od-id="zoom-controls">
          <button type="button" data-act="zoom-in" aria-label={t('zoomIn')} onClick={() => setView((v) => zoomAt(v, 1.15, w / 2, h / 2))}>
            <svg viewBox="0 0 20 20" aria-hidden="true">
              <path d="M10 4v12M4 10h12" />
            </svg>
          </button>
          <button type="button" data-act="zoom-out" aria-label={t('zoomOut')} onClick={() => setView((v) => zoomAt(v, 1 / 1.15, w / 2, h / 2))}>
            <svg viewBox="0 0 20 20" aria-hidden="true">
              <path d="M4 10h12" />
            </svg>
          </button>
          <button type="button" data-act="zoom-fit" aria-label={t('zoomFit')} onClick={fit}>
            <svg viewBox="0 0 20 20" aria-hidden="true">
              <path d="M4 8V4h4M16 12v4h-4M12 4h4v4M8 16H4v-4" />
            </svg>
          </button>
        </div>
      </div>

      <p className="hint-strip" id="hint-strip">
        {t('hint')}
      </p>
    </section>
  );
}
